import { AuthService } from './authService';
import { StcTransactionDto } from '../types/transactionHistory';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class TransactionHistoryService {
  /**
   * Gets the current company number from the authenticated user's token
   * @returns Company number
   * @throws Error if no company data is available
   */
  private getCompanyNo(): string {
    const userData = AuthService.getUserDataFromToken();
    if (!userData?.companyNo) {
      throw new Error('No company data available');
    }
    return userData.companyNo;
  }

  /**
   * Get transaction history with optional filters
   * @param fromDate Start date for the transaction history
   * @param toDate End date for the transaction history
   * @param warehouseId Optional warehouse ID filter
   * @param itemNo Optional item number filter
   * @param transType Optional transaction type filter
   * @param operatorId Optional operator ID filter
   * @returns Promise with array of transaction DTOs
   */
  async getTransactionHistory(
    fromDate: Date,
    toDate: Date,
    warehouseId?: string,
    itemNo?: string,
    transType?: number,
    operatorId?: string
  ): Promise<StcTransactionDto[]> {
    const companyNo = this.getCompanyNo();
    const params = new URLSearchParams();
    params.append('fromDate', fromDate.toISOString());
    params.append('toDate', toDate.toISOString());

    if (warehouseId && warehouseId.trim() !== '') {
      params.append('warehouseId', warehouseId);
    }
    if (itemNo && itemNo.trim() !== '') {
      params.append('itemNo', itemNo);
    }
    if (transType !== null && transType !== undefined) {
      params.append('transType', transType.toString());
    }
    if (operatorId && operatorId.trim() !== '') {
      params.append('operatorId', operatorId);
    }

    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/${companyNo}/stctransaction/history?${params.toString()}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to get transaction history: ${response.status} ${response.statusText}. ${errorText}`);
    }

    return await response.json();
  }
}

export const transactionHistoryService = new TransactionHistoryService();
